import { z } from 'zod';
import {
  EmailTemplateDoc,
  IEmailContentType,
  ITemplate,
  TemplateCategoryDoc,
  TemplateValidationResult
} from '@publishing-engine/domain/models/email-templates';
import axiosInstance from './client.js';
import {
  createEmailTemplateSchema,
  patchEmailTemplateSchema,
  templateCategoriesBodySchema
} from '@publishing-engine/domain/validation';
import { IDomainCommandType } from '@publishing-engine/domain/actions/v1/commands/index.js';
import {
  IRemindersConfigRouteResponse,
  IRemindersJournalConfigRouteResponse,
  ReminderConfigsRoute,
  ReminderConfigsRouteForJournals
} from '@publishing-engine/submission-service/src/endpoints/api.response-types.js';
import { GetGlobalTemplatesByEvent } from '@publishing-engine/email-service/src/routes/response-types';
import { VarDesc } from '../components/TemplateVariablesList';

const getProxyRoutePath = (organizationId: string) => {
  return `/email-service/${organizationId}`;
};

export function getTemplateByTemplateId(
  templateId: string,
  organizationId: string
) {
  return axiosInstance
    .get<EmailTemplateDoc & { updatedAt: string; createdAt: string }>(
      `/email-templates/organizations/${organizationId}/${templateId}`
    )
    .then(r => r.data);
}

type IListTemplatesByCommand = {
  organizationId: string;
  commandName: IDomainCommandType;
  query?: {
    subtype?: string;
    action?: string;
    tag?: string;
    customizable?: boolean;
  };
};
export function getAllTemplatesByCommand({
  organizationId,
  commandName,
  query
}: IListTemplatesByCommand) {
  return axiosInstance
    .get<{ data: EmailTemplateDoc[]; total: number }>(
      `/email-templates/organizations/${organizationId}/command/${commandName}`,
      {
        params: query ? query : {}
      }
    )
    .then(r => r.data);
}

type IEmailTemplatePreviewProps = {
  template: IEmailContentType;
  event?: Partial<{
    organizationId: string;
    submissionContextId: string;
    submissionId: string;
    type: string;
    subType: string;
    formPayload?: unknown;
  }>;
  test?: boolean;
};

export function getEmailTemplatePreview({
  template,
  event,
  test
}: IEmailTemplatePreviewProps) {
  return axiosInstance
    .post('/email/preview', { template, event, test })
    .then(r => r.data);
}

export type IListTemplatesQuery = {
  event?: string[];
  submissionContextId?: string[];
  category?: string[];
  scope?: string[];
  search?: string;
  active?: string[];
  phases?: string[];
  skip?: number;
  limit?: number;
};

export function getAllTemplates(
  organizationId: string,
  body: IListTemplatesQuery
) {
  return axiosInstance
    .post<{
      data: (EmailTemplateDoc & { updatedAt: string })[];
      total: number;
    }>(
      `${getProxyRoutePath(
        organizationId
      )}/email-templates/organizations/${organizationId}/fetch`,
      body
    )
    .then(r => r.data);
}

export function exportEmailTemplates(organizationId: string) {
  return axiosInstance
    .get<ArrayBuffer>(
      `/email-templates/organizations/${organizationId}/export`,
      {
        responseType: 'arraybuffer',
        headers: {
          Accept: 'text/csv'
        }
      }
    )
    .then(r => r);
}

export type IValidateDefaultTemplate = {
  organizationId: string;
  validateData: {
    event: string;
    default: boolean;
    tag?: string;
  };
};

export function validateDefaultTemplate({
  organizationId,
  validateData
}: IValidateDefaultTemplate) {
  return axiosInstance
    .post(
      `${getProxyRoutePath(organizationId)}/email-templates/validate/default`,
      validateData
    )
    .then(r => r.data);
}

export type ICreateTemplatePayload = z.infer<typeof createEmailTemplateSchema>;
export function createEmailTemplate(
  organizationId: string,
  body: ICreateTemplatePayload
) {
  return axiosInstance
    .post<EmailTemplateDoc>(
      `${getProxyRoutePath(
        organizationId
      )}/email-templates/organizations/${organizationId}`,
      body
    )
    .then(r => r.data);
}

export type IUpdateTemplatePayload = z.infer<typeof patchEmailTemplateSchema>;
export function editEmailTemplateById(
  templateId: string,
  organizationId: string,
  body: Partial<IUpdateTemplatePayload>
) {
  return axiosInstance
    .patch<EmailTemplateDoc>(
      `${getProxyRoutePath(
        organizationId
      )}/email-templates/organizations/${organizationId}/${templateId}`,
      body
    )
    .then(r => r.data);
}

export function deleteEmailTemplateById(id: string, organizationId: string) {
  return axiosInstance
    .delete<{}>(
      `${getProxyRoutePath(
        organizationId
      )}/email-templates/organizations/${organizationId}/${id}`
    )
    .then(r => r.data);
}

export function listEmailTemplateCategories(
  organizationId: string,
  query: IListTemplatesQuery
) {
  return axiosInstance
    .get<{ data: TemplateCategoryDoc[]; total: number }>(
      `${getProxyRoutePath(
        organizationId
      )}/template-categories/organizations/${organizationId}`,
      {
        params: query
      }
    )
    .then(r => r.data);
}

export function getEmailCategoryById(id: string, organizationId: string) {
  return axiosInstance
    .get<TemplateCategoryDoc>(
      `${getProxyRoutePath(
        organizationId
      )}/template-categories/organizations/${organizationId}/${id}`
    )
    .then(r => r.data);
}

export type ICreateTemplateCategoryPayload = z.infer<
  typeof templateCategoriesBodySchema
>;
export function addEmailCategory(
  organizationId: string,
  body: ICreateTemplateCategoryPayload
) {
  return axiosInstance
    .post<TemplateCategoryDoc>(
      `${getProxyRoutePath(
        organizationId
      )}/template-categories/organizations/${organizationId}`,
      body
    )
    .then(r => r.data);
}

export function updateEmailCategory(
  id: string,
  organizationId: string,
  body: ICreateTemplateCategoryPayload
) {
  return axiosInstance
    .patch<TemplateCategoryDoc>(
      `${getProxyRoutePath(
        organizationId
      )}/template-categories/organizations/${organizationId}/${id}`,
      body
    )
    .then(r => r.data);
}

export function deleteEmailCategory(id: string, organizationId: string) {
  return axiosInstance
    .delete<TemplateCategoryDoc>(
      `${getProxyRoutePath(
        organizationId
      )}/template-categories/organizations/${organizationId}/${id}`
    )
    .then(r => r.data);
}

export const getOrganizationEmailTemplateVariables = (organizationId: string) =>
  axiosInstance
    .get<VarDesc[]>(`/template-variables/organizations/${organizationId}`)
    .then(r => r.data);

export interface EventItem {
  event: string;
  eventKey: string;
  eventDisplayName: string;
  description: string;
  subEventCategory?: 'subtype' | 'action';
  subEventValue?: string;
  personalizable?: boolean;
}

export type EventLabel = { label: string; enabled: boolean };

interface EventsResponse {
  events: EventItem[];
  labels: Record<string, EventLabel>;
}

export async function getEmailEventsWithSubEvents(
  organizationId: string
): Promise<EventsResponse> {
  const response = await axiosInstance.get<EventsResponse>(
    `/organizations/${organizationId}/email-events`
  );
  return response.data;
}

export async function getReminderConfigsByOrgId(organizationId: string) {
  const response = await axiosInstance.get<IRemindersConfigRouteResponse[]>(
    ReminderConfigsRoute.build({ id: organizationId })
  );
  return response.data;
}

export async function getReminderConfigsByOrgIdAndJournalId(
  organizationId: string,
  submissionContextId: string
) {
  const response = await axiosInstance.get<
    IRemindersJournalConfigRouteResponse[]
  >(
    ReminderConfigsRouteForJournals.build({
      id: organizationId,
      ctxId: submissionContextId
    })
  );
  return response.data;
}

export function listGlobalTemplatesByEvent(
  organizationId: string,
  eventName: string,
  state?: string
) {
  const encodedEventName = encodeURIComponent(eventName);

  return axiosInstance
    .get<{ data: EmailTemplateDoc[] }>(
      `${getProxyRoutePath(organizationId)}${GetGlobalTemplatesByEvent.build({
        organizationId,
        eventName: encodedEventName
      })}`,
      {
        params: state ? { state } : undefined
      }
    )
    .then(r => r.data);
}

export async function getRejectionReasons(organizationId: string) {
  const response = await axiosInstance.get<{ rejectionReasons: string[] }>(
    `/organizations/${organizationId}/rejection-reasons`
  );
  return response.data;
}

export async function validateEmailTemplate(
  organizationId: string,
  template: ITemplate
) {
  const response = await axiosInstance.post<TemplateValidationResult>(
    `${getProxyRoutePath(organizationId)}/template-validator`,
    template
  );
  return response.data;
}
